import {
  imagePointToGround,
  pointInPolygonXZ,
  projectedActorBounds,
  screenSpaceSeparationPx,
  type BackgroundLayoutDefinition,
  type NormalizedPoint,
  type ProjectedActorBounds,
  type ViewportSize,
  type WorldPoint,
} from './videoBackgroundLayout';

export type BlockingScenarioId = 'approach' | 'conversation' | 'group' | 'crossing' | 'observer';

export interface BlockingScenarioDefinition {
  id: BlockingScenarioId;
  label: string;
  summary: string;
}

export const BLOCKING_SCENARIOS: BlockingScenarioDefinition[] = [
  { id: 'approach', label: 'Approach + background life', summary: 'One NPC is available while the rest keep moving.' },
  { id: 'conversation', label: 'Join the conversation', summary: 'A two-person exchange leaves room for the player to join.' },
  { id: 'group', label: 'Small group beat', summary: 'A loose group shares a space with action-led facing.' },
  { id: 'crossing', label: 'Crossing traffic', summary: 'Two routes pass through the stage without collisions.' },
  { id: 'observer', label: 'Anchor observer', summary: 'One NPC watches an environmental anchor as life passes by.' },
];

// A small gap is technically non-overlapping but still reads as one merged
// silhouette on a phone. Keep deliberate breathing room between blocks.
export const PORTRAIT_MIN_SCREEN_GAP_PX = 5;
export const PORTRAIT_ACTOR_HEIGHT = 1.58;
export const PORTRAIT_ACTOR_WIDTH = 0.52;

export type BlockingAction = 'player-approach' | 'conversation' | 'group-talk' | 'crossing' | 'observing' | 'idle';

export interface BlockingPlacement {
  id: string;
  feet: NormalizedPoint;
  worldPosition: WorldPoint;
  role: string;
  action: BlockingAction;
  zoneId: string;
  facingY: number;
  groundContact: number;
  movementTarget?: NormalizedPoint;
  path?: NormalizedPoint[];
}

interface SlotRecipe {
  id: string;
  role: string;
  action: BlockingAction;
  anchor?: string;
  zone?: string;
  lane?: string;
  t?: number;
  targetNpcId?: string;
  targetAnchor?: string;
  movementLane?: string;
  movementT?: number;
  movementTargetT?: number;
}

type EnvironmentKey = 'heritage' | 'campus';

const RECIPES: Record<EnvironmentKey, Record<BlockingScenarioId, SlotRecipe[]>> = {
  heritage: {
    approach: [
      { id: 'zoe', role: 'available at the lockers', action: 'player-approach', anchor: 'locker-bank', targetAnchor: 'player-entry' },
      { id: 'luna', role: 'passing traffic', action: 'crossing', lane: 'right-pass', t: 0.56, movementLane: 'right-pass', movementT: 0.42, movementTargetT: 0.78 },
      { id: 'dmitri', role: 'doorway idle', action: 'idle', anchor: 'right-doorway', targetAnchor: 'right-doorway' },
      { id: 'priya', role: 'far-hall observer', action: 'observing', anchor: 'far-door', targetAnchor: 'far-door' },
    ],
    conversation: [
      { id: 'zoe', role: 'conversation lead', action: 'conversation', anchor: 'locker-bank', targetNpcId: 'luna' },
      { id: 'luna', role: 'conversation partner', action: 'conversation', anchor: 'center-pass', targetNpcId: 'zoe' },
      { id: 'dmitri', role: 'passing traffic', action: 'crossing', lane: 'right-pass', t: 0.46, movementLane: 'right-pass', movementT: 0.76, movementTargetT: 0.42 },
      { id: 'priya', role: 'far-door observer', action: 'observing', anchor: 'far-door', targetAnchor: 'far-door' },
    ],
    group: [
      { id: 'zoe', role: 'group speaker', action: 'group-talk', zone: 'locker-cluster', targetNpcId: 'luna' },
      { id: 'luna', role: 'group listener', action: 'group-talk', zone: 'locker-cluster', targetNpcId: 'zoe' },
      { id: 'dmitri', role: 'group edge', action: 'group-talk', zone: 'locker-cluster', targetNpcId: 'zoe' },
      { id: 'priya', role: 'hall monitor', action: 'observing', anchor: 'far-door', targetAnchor: 'locker-bank' },
    ],
    crossing: [
      { id: 'zoe', role: 'left-to-right traffic', action: 'crossing', lane: 'left-pass', t: 0.64, movementLane: 'left-pass', movementT: 0.3, movementTargetT: 0.82 },
      { id: 'luna', role: 'right-to-left traffic', action: 'crossing', lane: 'right-pass', t: 0.22, movementLane: 'right-pass', movementT: 0.18, movementTargetT: 0.82 },
      { id: 'dmitri', role: 'locker idle', action: 'idle', anchor: 'locker-bank', targetAnchor: 'locker-bank' },
      { id: 'priya', role: 'doorway observer', action: 'observing', anchor: 'right-doorway', targetAnchor: 'center-pass' },
    ],
    observer: [
      { id: 'priya', role: 'watching the far door', action: 'observing', anchor: 'far-door', targetAnchor: 'far-door' },
      { id: 'zoe', role: 'wall idle', action: 'idle', zone: 'left-wall-idle', targetAnchor: 'locker-bank' },
      { id: 'luna', role: 'window-side traffic', action: 'crossing', lane: 'right-pass', t: 0.52, movementLane: 'right-pass', movementT: 0.42, movementTargetT: 0.78 },
      { id: 'dmitri', role: 'approaching from the hall', action: 'crossing', lane: 'center-pass', t: 0.7, movementLane: 'center-pass', movementT: 0.78, movementTargetT: 0.42 },
    ],
  },
  campus: {
    approach: [
      { id: 'zoe', role: 'available by the bench', action: 'player-approach', anchor: 'left-bench', targetAnchor: 'player-entry' },
      { id: 'luna', role: 'crossing the quad', action: 'crossing', lane: 'diagonal-crossing', t: 0.42, movementLane: 'diagonal-crossing', movementT: 0.3, movementTargetT: 0.72 },
      { id: 'dmitri', role: 'plaza idle', action: 'idle', zone: 'central-gathering', targetAnchor: 'central-plaza' },
      { id: 'priya', role: 'arcade observer', action: 'observing', anchor: 'right-arcade', targetAnchor: 'central-plaza' },
    ],
    conversation: [
      { id: 'zoe', role: 'conversation lead', action: 'conversation', anchor: 'left-bench', targetNpcId: 'luna' },
      { id: 'luna', role: 'conversation partner', action: 'conversation', anchor: 'central-plaza', targetNpcId: 'zoe' },
      { id: 'dmitri', role: 'crossing the background', action: 'crossing', lane: 'right-crossing', t: 0.44, movementLane: 'right-crossing', movementT: 0.3, movementTargetT: 0.72 },
      { id: 'priya', role: 'far-step observer', action: 'observing', anchor: 'far-steps', targetAnchor: 'central-plaza' },
    ],
    group: [
      { id: 'zoe', role: 'group speaker', action: 'group-talk', zone: 'central-gathering', targetNpcId: 'luna' },
      { id: 'luna', role: 'group listener', action: 'group-talk', zone: 'central-gathering', targetNpcId: 'zoe' },
      { id: 'dmitri', role: 'group edge', action: 'group-talk', zone: 'central-gathering', targetNpcId: 'zoe' },
      { id: 'priya', role: 'watching the steps', action: 'observing', anchor: 'far-steps', targetAnchor: 'central-plaza' },
    ],
    crossing: [
      { id: 'zoe', role: 'west-to-east crossing', action: 'crossing', lane: 'front-crossing', t: 0.32, movementLane: 'front-crossing', movementT: 0.18, movementTargetT: 0.82 },
      { id: 'luna', role: 'east-to-west crossing', action: 'crossing', lane: 'diagonal-crossing', t: 0.7, movementLane: 'diagonal-crossing', movementT: 0.82, movementTargetT: 0.18 },
      { id: 'dmitri', role: 'bench idle', action: 'idle', anchor: 'right-bench', targetAnchor: 'right-bench' },
      { id: 'priya', role: 'arcade observer', action: 'observing', anchor: 'left-arcade', targetAnchor: 'front-crossing' },
    ],
    observer: [
      { id: 'priya', role: 'watching the central plaza', action: 'observing', anchor: 'right-arcade', targetAnchor: 'central-plaza' },
      { id: 'zoe', role: 'bench idle', action: 'idle', anchor: 'left-bench', targetAnchor: 'left-bench' },
      { id: 'luna', role: 'long-route traffic', action: 'crossing', lane: 'center-route', t: 0.5, movementLane: 'center-route', movementT: 0.38, movementTargetT: 0.76 },
      { id: 'dmitri', role: 'arcade-to-plaza traffic', action: 'crossing', lane: 'left-crossing', t: 0.66, movementLane: 'left-crossing', movementT: 0.78, movementTargetT: 0.38 },
    ],
  },
};

function seeded(seed: number) {
  let value = seed >>> 0;
  return () => {
    value = (value * 1664525 + 1013904223) >>> 0;
    return value / 0x100000000;
  };
}

function lerpPoint(a: NormalizedPoint, b: NormalizedPoint, t: number): NormalizedPoint {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

function samplePolyline(points: NormalizedPoint[], t: number): NormalizedPoint {
  const lengths = points.slice(1).map((point, index) => Math.hypot(point[0] - points[index][0], point[1] - points[index][1]));
  const total = lengths.reduce((sum, length) => sum + length, 0);
  let remaining = total * Math.max(0, Math.min(1, t));
  for (let index = 0; index < lengths.length; index += 1) {
    if (remaining <= lengths[index]) return lerpPoint(points[index], points[index + 1], remaining / lengths[index]);
    remaining -= lengths[index];
  }
  return points[points.length - 1];
}

function polygonCentroid(polygon: NormalizedPoint[]): NormalizedPoint {
  const sum = polygon.reduce((total, point) => [total[0] + point[0], total[1] + point[1]], [0, 0]);
  return [sum[0] / polygon.length, sum[1] / polygon.length];
}

function pointInPolygon2d(point: NormalizedPoint, polygon: NormalizedPoint[]) {
  let inside = false;
  for (let index = 0, previous = polygon.length - 1; index < polygon.length; previous = index++) {
    const [x, y] = polygon[index];
    const [px, py] = polygon[previous];
    const intersects = ((y > point[1]) !== (py > point[1]))
      && point[0] < ((px - x) * (point[1] - y)) / ((py - y) || Number.EPSILON) + x;
    if (intersects) inside = !inside;
  }
  return inside;
}

function getPoint(
  layout: BackgroundLayoutDefinition,
  point: NormalizedPoint,
  viewport?: ViewportSize,
  frame?: [number, number, number, number],
) {
  // Authored points are in the background's 1000x1000 analysis space. Keep
  // that calibration stable when the viewport changes; the viewport camera is
  // used later for projected bounds and screen-space validation.
  void viewport;
  void frame;
  return imagePointToGround(point, layout.camera);
}

export function blockingPointToWorld(
  layout: BackgroundLayoutDefinition,
  point: NormalizedPoint,
  viewport?: ViewportSize,
) {
  return getPoint(layout, point, viewport, viewport ? layout.analysis.cameraFrame.mobile : undefined);
}

function choosePoint(
  layout: BackgroundLayoutDefinition,
  recipe: SlotRecipe,
  placements: BlockingPlacement[],
  random: () => number,
  _scenarioId: BlockingScenarioId,
  viewport?: ViewportSize,
): { feet: NormalizedPoint; worldPosition: WorldPoint; zoneId: string } {
  const analysis = layout.analysis;
  const frame = analysis.cameraFrame.desktop;
  const walkableFloor = analysis.floorPolygon
    .map((point) => getPoint(layout, point))
    .filter((point): point is WorldPoint => Boolean(point));
  const lane = recipe.lane ? analysis.trafficLanes.find((item) => item.id === recipe.lane) : undefined;
  const zone = recipe.zone ? analysis.zones.find((item) => item.id === recipe.zone) : undefined;
  const anchor = recipe.anchor ? analysis.interactionAnchors.find((item) => item.id === recipe.anchor) : undefined;
  const base = anchor?.point
    ?? (lane ? samplePolyline(lane.points, recipe.t ?? 0.5) : undefined)
    ?? (zone ? polygonCentroid(zone.polygon) : undefined);
  if (!base) throw new Error(`Blocking recipe has no spatial source for ${recipe.id}`);

  const candidates: NormalizedPoint[] = [base];
  if (lane) {
    const laneWidth = lane.width * 0.28;
    candidates.push(
      samplePolyline(lane.points, (recipe.t ?? 0.5) + 0.04),
      samplePolyline(lane.points, (recipe.t ?? 0.5) - 0.04),
      [base[0] - laneWidth, base[1]],
      [base[0] + laneWidth, base[1]],
      [base[0] - laneWidth * 1.55, base[1] + 18],
      [base[0] + laneWidth * 1.55, base[1] + 18],
    );
  }
  if (zone) {
    const [cx, cy] = polygonCentroid(zone.polygon);
    candidates.push(...zone.polygon.map((point) => lerpPoint(point, [cx, cy], 0.35)));
  }
  candidates.push(
    [base[0] + (random() - 0.5) * 42, base[1] + (random() - 0.5) * 32],
    [base[0] - 30, base[1] + 18],
    [base[0] + 30, base[1] + 18],
  );
  if (viewport && viewport.width >= viewport.height) {
    for (const depth of [0.18, 0.3, 0.44, 0.58, 0.72, 0.84]) {
      for (const lateral of [0.14, 0.28, 0.42, 0.58, 0.72, 0.86]) {
        candidates.push([
          frame[0] + frame[2] * lateral,
          frame[1] + frame[3] * depth,
        ]);
      }
    }
  }

  const actorHeight = recipe.id === 'dmitri' ? 1.88
    : recipe.id === 'luna' ? 1.7
      : recipe.id === 'priya' ? 1.66
        : 1.58;
  const actorWidth = 0.62;

  for (const candidate of candidates) {
    if (candidate[0] < frame[0] + analysis.cameraFrame.edgeMargin
      || candidate[0] > frame[0] + frame[2] - analysis.cameraFrame.edgeMargin
      || candidate[1] < frame[1] + analysis.cameraFrame.edgeMargin
      || candidate[1] > frame[1] + frame[3] - analysis.cameraFrame.edgeMargin) continue;
    if (analysis.blockedRegions.some((region) => pointInPolygon2d(candidate, region.polygon))) continue;
    const worldPosition = getPoint(layout, candidate);
    if (!worldPosition || !pointInPolygonXZ(worldPosition, walkableFloor)) continue;
    const tooClose = placements.some((placement) => (
      Math.hypot(worldPosition[0] - placement.worldPosition[0], worldPosition[2] - placement.worldPosition[2])
      < Math.max(0.95, layout.minPairSpacing * 0.78)
    ));
    if (tooClose) continue;
    if (viewport && viewport.width >= viewport.height) {
      const bounds = projectedActorBounds(worldPosition, actorHeight, actorWidth, layout.camera, viewport);
      const visible = bounds.inFront
        && bounds.left >= 12
        && bounds.top >= 12
        && bounds.right <= viewport.width - 12
        && bounds.bottom <= viewport.height - 12
        && bounds.heightPx >= 20;
      const separated = placements.every((placement) => {
        const otherHeight = placement.id === 'dmitri' ? 1.88
          : placement.id === 'luna' ? 1.7
            : placement.id === 'priya' ? 1.66
              : 1.58;
        return screenSpaceSeparationPx(
          bounds,
          projectedActorBounds(placement.worldPosition, otherHeight, actorWidth, layout.camera, viewport),
        ) >= PORTRAIT_MIN_SCREEN_GAP_PX;
      });
      if (visible && separated) return { feet: candidate, worldPosition, zoneId: recipe.zone ?? recipe.anchor ?? recipe.lane ?? 'walkable-floor' };
      continue;
    }
    return { feet: candidate, worldPosition, zoneId: recipe.zone ?? recipe.anchor ?? recipe.lane ?? 'walkable-floor' };
  }
  throw new Error(`Unable to find a clear blocking point for ${recipe.id}`);
}

interface CandidatePoint {
  feet: NormalizedPoint;
  worldPosition: WorldPoint;
  zoneId: string;
}

function crossingSideFor(recipe: SlotRecipe): 'left' | 'right' | null {
  if (recipe.action !== 'crossing') return null;
  if (/right-to-left|east-to-west/i.test(recipe.role) || /right/i.test(recipe.lane ?? '')) return 'right';
  if (/left-to-right|west-to-east/i.test(recipe.role) || /left|front/i.test(recipe.lane ?? '')) return 'left';
  return (recipe.t ?? 0) > 0.5 ? 'right' : 'left';
}

function addUniqueSemanticPoint(points: NormalizedPoint[], point: NormalizedPoint) {
  if (!points.some((existing) => Math.hypot(existing[0] - point[0], existing[1] - point[1]) < 0.5)) points.push(point);
}

function generatePortraitCandidates(
  layout: BackgroundLayoutDefinition,
  recipe: SlotRecipe,
  placements: BlockingPlacement[],
  random: () => number,
  viewport: ViewportSize,
): CandidatePoint[] {
  const frame = layout.analysis.cameraFrame.mobile;
  const points: NormalizedPoint[] = [];
  const addRelative = (origin: NormalizedPoint, offsets: NormalizedPoint[]) => offsets.forEach(([x, y]) => (
    addUniqueSemanticPoint(points, [origin[0] + x * frame[2], origin[1] + y * frame[3]])
  ));
  const anchor = recipe.anchor ? layout.analysis.interactionAnchors.find((item) => item.id === recipe.anchor) : undefined;
  const lane = recipe.lane ? layout.analysis.trafficLanes.find((item) => item.id === recipe.lane) : undefined;
  const zone = recipe.zone ? layout.analysis.zones.find((item) => item.id === recipe.zone) : undefined;
  const base = anchor?.point ?? (lane ? samplePolyline(lane.points, recipe.t ?? 0.5) : undefined) ?? (zone ? polygonCentroid(zone.polygon) : undefined);
  if (!base) throw new Error(`Blocking recipe has no spatial source for ${recipe.id}`);
  const interactionZone = layout.analysis.zones
    .filter((item) => item.kind === 'interaction')
    .sort((first, second) => {
      const origin = anchor?.point ?? base;
      const a = polygonCentroid(first.polygon);
      const b = polygonCentroid(second.polygon);
      return Math.hypot(a[0] - origin[0], a[1] - origin[1]) - Math.hypot(b[0] - origin[0], b[1] - origin[1]);
    })[0];
  const semanticOrigin = zone ? polygonCentroid(zone.polygon) : polygonCentroid(interactionZone?.polygon ?? [base]);
  const target = recipe.targetNpcId ? placements.find((item) => item.id === recipe.targetNpcId) : undefined;

  if (recipe.action === 'conversation') {
    const origin = target?.feet ?? semanticOrigin;
    if (!target) addUniqueSemanticPoint(points, base);
    addRelative(origin, [[-0.12, -0.04], [0.12, -0.04], [-0.16, 0.04], [0.16, 0.04], [-0.08, 0.1], [0.08, 0.1]]);
  } else if (recipe.action === 'group-talk') {
    const clusterRegion = zone ?? interactionZone;
    const regionXs = clusterRegion?.polygon.map((point) => point[0]) ?? [frame[0], frame[0] + frame[2]];
    const regionYs = clusterRegion?.polygon.map((point) => point[1]) ?? [frame[1], frame[1] + frame[3]];
    const regionCenterY = (Math.min(...regionYs) + Math.max(...regionYs)) / 2;
    const lateralStep = Math.min(frame[2] * 0.055, (Math.max(...regionXs) - Math.min(...regionXs)) * 0.25);
    const depthStep = Math.min(frame[3] * 0.14, (Math.max(...regionYs) - Math.min(...regionYs)) * 0.48);
    const clusterCenter: NormalizedPoint = [frame[0] + frame[2] / 2, regionCenterY];
    const roleOffset = /speaker/i.test(recipe.role)
      ? [0, depthStep]
      : /listener/i.test(recipe.role)
        ? [-lateralStep, -depthStep]
        : /edge/i.test(recipe.role)
          ? [lateralStep, -depthStep]
          : [0, 0];
    addUniqueSemanticPoint(points, [clusterCenter[0] + roleOffset[0], clusterCenter[1] + roleOffset[1]]);
    addRelative(semanticOrigin, [[-0.14, -0.08], [-0.08, 0.05], [0, -0.12], [0.08, 0.04], [0.14, -0.06], [-0.18, 0.1], [0.18, 0.1]]);
    if (zone) zone.polygon.forEach((point) => addUniqueSemanticPoint(points, lerpPoint(point, semanticOrigin, 0.35)));
  } else if (recipe.action === 'crossing' && lane) {
    for (const t of [0.06, 0.14, 0.22, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9]) {
      const point = samplePolyline(lane.points, t);
      addUniqueSemanticPoint(points, point);
      addUniqueSemanticPoint(points, [point[0] - lane.width * 0.55, point[1]]);
      addUniqueSemanticPoint(points, [point[0] + lane.width * 0.55, point[1]]);
    }
  } else if (anchor) {
    addRelative(anchor.point, [[0.25, -0.15], [0.1, -0.15], [-0.08, -0.15], [-0.16, -0.137], [-0.1, -0.137], [-0.18, -0.18], [0.18, -0.18], [-0.24, -0.08], [0.24, -0.08], [-0.1, -0.08], [-0.12, -0.2], [-0.24, -0.02], [0.24, -0.02], [-0.18, 0.06], [0.18, 0.06]]);
  }

  addUniqueSemanticPoint(points, base);
  addRelative(base, [[-0.28, -0.12], [-0.22, -0.08], [-0.16, -0.04], [-0.1, 0.04], [0.1, 0.04], [0.16, -0.04], [0.22, -0.08], [0.28, -0.12], [-0.24, 0.12], [-0.12, 0.16], [0.12, 0.16], [0.24, 0.12]]);
  for (const depth of [0.12, 0.2, 0.3, 0.42, 0.56, 0.7, 0.84]) {
    for (const lateral of [0.14, 0.28, 0.42, 0.5, 0.58, 0.72, 0.86]) {
      addUniqueSemanticPoint(points, [frame[0] + frame[2] * lateral, frame[1] + frame[3] * depth]);
    }
  }
  addRelative(base, [[(random() - 0.5) * 0.2, (random() - 0.5) * 0.14]]);

  const floor = layout.analysis.floorPolygon
    .map((point) => getPoint(layout, [
      Math.max(frame[0], Math.min(frame[0] + frame[2], point[0])),
      Math.max(frame[1], Math.min(frame[1] + frame[3], point[1])),
    ], viewport, frame))
    .filter((point): point is WorldPoint => Boolean(point));
  const candidates: CandidatePoint[] = [];
  for (const point of points.slice(0, 72)) {
    if (point[0] < frame[0] + layout.analysis.cameraFrame.edgeMargin
      || point[0] > frame[0] + frame[2] - layout.analysis.cameraFrame.edgeMargin
      || point[1] < frame[1] + layout.analysis.cameraFrame.edgeMargin
      || point[1] > frame[1] + frame[3] - layout.analysis.cameraFrame.edgeMargin) continue;
    if (layout.analysis.blockedRegions.some((region) => pointInPolygon2d(point, region.polygon))) continue;
    const worldPosition = getPoint(layout, point, viewport, frame);
    if (!worldPosition || !pointInPolygonXZ(worldPosition, floor)) continue;
    if (placements.some((placement) => Math.hypot(worldPosition[0] - placement.worldPosition[0], worldPosition[2] - placement.worldPosition[2]) < 0.72)) continue;
    candidates.push({ feet: point, worldPosition, zoneId: recipe.zone ?? recipe.anchor ?? recipe.lane ?? 'walkable-floor' });
  }
  return candidates;
}

function portraitRelationshipValid(
  layout: BackgroundLayoutDefinition,
  recipe: SlotRecipe,
  placements: BlockingPlacement[],
  candidate: CandidatePoint,
  scenarioId: BlockingScenarioId,
  viewport: ViewportSize,
) {
  const bounds = projectedActorBounds(candidate.worldPosition, PORTRAIT_ACTOR_HEIGHT, PORTRAIT_ACTOR_WIDTH, layout.camera, viewport);
  const all = [...placements, {
    id: recipe.id,
    feet: candidate.feet,
    worldPosition: candidate.worldPosition,
    role: recipe.role,
    action: recipe.action,
    zoneId: candidate.zoneId,
    facingY: 0,
    groundContact: 0,
  } as BlockingPlacement];
  const visible = bounds.inFront && bounds.left >= 18 && bounds.top >= 18 && bounds.right <= viewport.width - 18 && bounds.bottom <= viewport.height - 18 && bounds.heightPx >= 32;
  const separated = placements.every((placement) => screenSpaceSeparationPx(bounds, projectedActorBounds(placement.worldPosition, PORTRAIT_ACTOR_HEIGHT, PORTRAIT_ACTOR_WIDTH, layout.camera, viewport)) >= PORTRAIT_MIN_SCREEN_GAP_PX);
  if (!visible || !separated) return false;

  const target = recipe.targetNpcId ? all.find((placement) => placement.id === recipe.targetNpcId) : undefined;
  const targetBounds = target ? projectedActorBounds(target.worldPosition, PORTRAIT_ACTOR_HEIGHT, PORTRAIT_ACTOR_WIDTH, layout.camera, viewport) : undefined;
  if (recipe.action === 'conversation') {
    if (targetBounds) {
      const distance = Math.hypot(bounds.centerX - targetBounds.centerX, bounds.centerY - targetBounds.centerY);
      if (distance > 155 || Math.abs(bounds.centerX - targetBounds.centerX) < 50) return false;
      const midpoint = (bounds.centerX + targetBounds.centerX) / 2;
      if (midpoint < 90 || midpoint > viewport.width - 90) return false;
    } else if (bounds.centerX < 70 || bounds.centerX > viewport.width * 0.5) return false;
  }
  const conversationBounds = all.filter((placement) => placement.action === 'conversation').map((placement) => projectedActorBounds(placement.worldPosition, PORTRAIT_ACTOR_HEIGHT, PORTRAIT_ACTOR_WIDTH, layout.camera, viewport));
  if (scenarioId === 'conversation' && recipe.action !== 'conversation' && conversationBounds.length === 2) {
    const left = Math.min(...conversationBounds.map((item) => item.centerX));
    const right = Math.max(...conversationBounds.map((item) => item.centerX));
    if (!(bounds.centerX < left - 12 || bounds.centerX > right + 12 || bounds.centerY < Math.min(...conversationBounds.map((item) => item.top)) - 24)) return false;
  }
  const groupBounds = all.filter((placement) => placement.action === 'group-talk').map((placement) => projectedActorBounds(placement.worldPosition, PORTRAIT_ACTOR_HEIGHT, PORTRAIT_ACTOR_WIDTH, layout.camera, viewport));
  if (scenarioId === 'group' && recipe.action === 'group-talk' && groupBounds.length > 1) {
    if (groupBounds.some((item) => Math.hypot(bounds.centerX - item.centerX, bounds.centerY - item.centerY) > 210)) return false;
    const span = Math.max(...groupBounds.map((item) => item.centerX), bounds.centerX) - Math.min(...groupBounds.map((item) => item.centerX), bounds.centerX);
    if (span > 240) return false;
    if (targetBounds && Math.hypot(bounds.centerX - targetBounds.centerX, bounds.centerY - targetBounds.centerY) > 205) return false;
  }
  if (scenarioId === 'group' && groupBounds.length === 3 && !portraitGroupClusterValid(groupBounds, viewport)) return false;
  if (recipe.action === 'crossing') {
    const side = crossingSideFor(recipe);
    if (side === 'left' && bounds.centerX > 170) return false;
    if (side === 'right' && bounds.centerX < viewport.width - 170) return false;
    const lane = recipe.movementLane ? layout.analysis.trafficLanes.find((item) => item.id === recipe.movementLane) : undefined;
    const targetPoint = lane ? getPoint(layout, samplePolyline(lane.points, recipe.movementTargetT ?? 0.8), viewport, layout.analysis.cameraFrame.mobile) : undefined;
    const targetBoundsForMovement = targetPoint ? projectedActorBounds(targetPoint, PORTRAIT_ACTOR_HEIGHT, PORTRAIT_ACTOR_WIDTH, layout.camera, viewport) : undefined;
    if (/left-to-right|west-to-east/i.test(recipe.role) && targetBoundsForMovement && targetBoundsForMovement.centerX - bounds.centerX < 18) return false;
    if (/right-to-left|east-to-west/i.test(recipe.role) && targetBoundsForMovement && bounds.centerX - targetBoundsForMovement.centerX < 18) return false;
  }
  return true;
}

function portraitGroupClusterValid(bounds: ProjectedActorBounds[], viewport: ViewportSize) {
  if (bounds.length !== 3) return false;
  const minX = Math.min(...bounds.map((bound) => bound.centerX));
  const maxX = Math.max(...bounds.map((bound) => bound.centerX));
  const minY = Math.min(...bounds.map((bound) => bound.centerY));
  const maxY = Math.max(...bounds.map((bound) => bound.centerY));
  const diameter = Math.max(...bounds.flatMap((first, firstIndex) => bounds.slice(firstIndex + 1).map((second) => (
    Math.hypot(first.centerX - second.centerX, first.centerY - second.centerY)
  ))));
  const nearestDistance = bounds.map((first, firstIndex) => Math.min(...bounds
    .filter((_, secondIndex) => secondIndex !== firstIndex)
    .map((second) => Math.hypot(first.centerX - second.centerX, first.centerY - second.centerY))));
  const connected = nearestDistance.every((distance) => distance <= 170);
  const [first, second, third] = bounds;
  const area = Math.abs(
    (second.centerX - first.centerX) * (third.centerY - first.centerY)
      - (second.centerY - first.centerY) * (third.centerX - first.centerX),
  ) * 0.5;
  const centroidX = bounds.reduce((sum, bound) => sum + bound.centerX, 0) / bounds.length;
  const centroidY = bounds.reduce((sum, bound) => sum + bound.centerY, 0) / bounds.length;
  const rearMemberCount = bounds.filter((bound) => bound.centerY <= centroidY - 12).length;
  const pocketX = Math.max(36, Math.min(viewport.width - 36, centroidX));
  const pocketY = maxY + 72;
  const openPlayerPocket = pocketY <= viewport.height - 72 && bounds.every((bound) => (
    pocketX < bound.left - 12
      || pocketX > bound.right + 12
      || pocketY < bound.top - 12
      || pocketY > bound.bottom + 12
  ));
  return maxX - minX <= 175
    && diameter <= 215
    && connected
    && area >= 1_000
    && maxY - minY >= 60
    && rearMemberCount >= 1
    && openPlayerPocket;
}

function portraitGroupSceneScore(
  placements: BlockingPlacement[],
  layout: BackgroundLayoutDefinition,
  viewport: ViewportSize,
) {
  const groupBounds = placements
    .filter((placement) => placement.action === 'group-talk')
    .map((placement) => projectedActorBounds(placement.worldPosition, PORTRAIT_ACTOR_HEIGHT, PORTRAIT_ACTOR_WIDTH, layout.camera, viewport));
  if (!portraitGroupClusterValid(groupBounds, viewport)) return Number.NEGATIVE_INFINITY;
  const minX = Math.min(...groupBounds.map((bound) => bound.centerX));
  const maxX = Math.max(...groupBounds.map((bound) => bound.centerX));
  const minY = Math.min(...groupBounds.map((bound) => bound.centerY));
  const maxY = Math.max(...groupBounds.map((bound) => bound.centerY));
  const centroidX = groupBounds.reduce((sum, bound) => sum + bound.centerX, 0) / groupBounds.length;
  const foreground = groupBounds.find((bound) => bound.centerY === maxY);
  const centrality = foreground ? 1 - Math.min(1, Math.abs(foreground.centerX - centroidX) / Math.max(1, (maxX - minX) / 2)) : 0;
  const secondary = placements
    .filter((placement) => placement.action !== 'group-talk')
    .map((placement) => projectedActorBounds(placement.worldPosition, PORTRAIT_ACTOR_HEIGHT, PORTRAIT_ACTOR_WIDTH, layout.camera, viewport));
  const observerAtEdge = secondary.some((bound) => (
    bound.centerX <= minX - 12 || bound.centerX >= maxX + 12 || bound.centerY <= minY - 24
  ));
  if (!observerAtEdge) return Number.NEGATIVE_INFINITY;
  const area = Math.abs(
    (groupBounds[1].centerX - groupBounds[0].centerX) * (groupBounds[2].centerY - groupBounds[0].centerY)
      - (groupBounds[1].centerY - groupBounds[0].centerY) * (groupBounds[2].centerX - groupBounds[0].centerX),
  ) * 0.5;
  return centrality * 180
    + (175 - (maxX - minX))
    + (215 - Math.max(...groupBounds.flatMap((first, firstIndex) => groupBounds.slice(firstIndex + 1).map((second) => (
      Math.hypot(first.centerX - second.centerX, first.centerY - second.centerY)
    )))))
    + area / 100
    + (maxY - minY);
}

function resolvePortraitScene(
  layout: BackgroundLayoutDefinition & { id?: string },
  recipes: SlotRecipe[],
  scenarioId: BlockingScenarioId,
  seed: number,
  viewport: ViewportSize,
) {
  const random = seeded(seed);
  const order = [...recipes].sort((first, second) => {
    const priority = (recipe: SlotRecipe) => {
      if (scenarioId === 'conversation') return recipe.action === 'conversation' ? 0 : recipe.id === 'priya' ? 2 : 1;
      if (scenarioId === 'group') return recipe.action === 'group-talk' ? 0 : 1;
      if (scenarioId === 'crossing') return recipe.action === 'crossing' ? 0 : recipe.id === 'priya' ? 1 : 2;
      if (scenarioId === 'approach') return recipe.action === 'player-approach' ? 0 : recipe.id === 'dmitri' ? 1 : recipe.id === 'priya' ? 3 : 2;
      return recipe.id === 'priya' ? 0 : recipe.action === 'crossing' ? 1 : recipe.id === 'dmitri' ? 2 : 3;
    };
    return priority(first) - priority(second);
  });
  let nodes = 0;
  let bestGroupResult: BlockingPlacement[] | null = null;
  let bestGroupScore = Number.NEGATIVE_INFINITY;
  const search = (index: number, placements: BlockingPlacement[]): BlockingPlacement[] | null => {
    if (++nodes > (scenarioId === 'group' ? 60_000 : 24_000)) return null;
    if (index === order.length) {
      if (scenarioId !== 'group') return placements;
      const score = portraitGroupSceneScore(placements, layout, viewport);
      if (score > bestGroupScore) {
        bestGroupScore = score;
        bestGroupResult = placements;
      }
      return null;
    }
    const recipe = order[index];
    const candidates = generatePortraitCandidates(layout, recipe, placements, random, viewport);
    for (const candidate of candidates) {
      if (!portraitRelationshipValid(layout, recipe, placements, candidate, scenarioId, viewport)) continue;
      const next = [...placements, {
        id: recipe.id,
        feet: candidate.feet,
        worldPosition: candidate.worldPosition,
        role: recipe.role,
        action: recipe.action,
        zoneId: candidate.zoneId,
        facingY: 0,
        groundContact: 0,
      }];
      const result = search(index + 1, next);
      if (result && scenarioId !== 'group') return result;
    }
    return null;
  };
  const result = search(0, []);
  const resolved = scenarioId === 'group' ? bestGroupResult : result;
  if (!resolved) throw new Error(`Unable to jointly resolve portrait blocking scene for ${layout.id ?? 'background'} ${scenarioId}`);
  return resolved;
}

function facingToward(from: WorldPoint, target: WorldPoint) {
  return Math.atan2(from[0] - target[0], from[2] - target[2]);
}

export function resolveBlockingScenario(
  layout: BackgroundLayoutDefinition & { id?: string },
  scenarioId: BlockingScenarioId,
  seed = 1,
  viewport?: ViewportSize,
): BlockingPlacement[] {
  const environmentId = layout.id as EnvironmentKey | undefined;
  if (!environmentId || !RECIPES[environmentId]) throw new Error('Semantic blocking recipes are authored for hallway and campus only');
  const random = seeded(seed);
  const recipes = RECIPES[environmentId][scenarioId];
  const portraitViewport = viewport && viewport.height > viewport.width ? viewport : undefined;
  const placements: BlockingPlacement[] = portraitViewport
    ? resolvePortraitScene(layout, recipes, scenarioId, seed, portraitViewport)
    : [];
  const solveOrder = portraitViewport ? [] : recipes
    ;
  solveOrder.forEach((recipe) => {
    const chosen = choosePoint(layout, recipe, placements, random, scenarioId, viewport);
      placements.push({
      id: recipe.id,
      feet: chosen.feet,
      worldPosition: chosen.worldPosition,
      role: recipe.role,
      action: recipe.action,
      zoneId: chosen.zoneId,
      facingY: 0,
        groundContact: 0,
      });
  });

  for (const recipe of recipes) {
    const placement = placements.find((item) => item.id === recipe.id);
    if (!placement) throw new Error(`Missing resolved blocking point for ${recipe.id}`);
    const targetPlacement = recipe.targetNpcId ? placements.find((item) => item.id === recipe.targetNpcId) : undefined;
    const targetAnchor = recipe.targetAnchor ? layout.analysis.interactionAnchors.find((item) => item.id === recipe.targetAnchor) : undefined;
    const groupTalkPlacements = recipes.filter((item) => item.action === 'group-talk')
      .map((item) => placements.find((candidate) => candidate.id === item.id)?.worldPosition)
      .filter((point): point is WorldPoint => Boolean(point));
    const groupCenter = groupTalkPlacements.length === 3
      ? groupTalkPlacements.reduce((sum, point) => [sum[0] + point[0], 0, sum[2] + point[2]], [0, 0, 0] as WorldPoint)
        .map((value) => value / 3) as WorldPoint
      : undefined;
    const targetWorld = recipe.action === 'group-talk' && groupCenter
      ? groupCenter
      : targetPlacement?.worldPosition ?? (targetAnchor
      ? blockingPointToWorld(layout, targetAnchor.point, viewport)
      : undefined);
    const lane = recipe.movementLane ? layout.analysis.trafficLanes.find((item) => item.id === recipe.movementLane) : undefined;
    if (lane) {
      const targetPoint = samplePolyline(lane.points, recipe.movementTargetT ?? 0.8);
      placement.movementTarget = targetPoint;
      placement.path = [placement.feet, targetPoint];
      const target = getPoint(layout, targetPoint, viewport, viewport ? layout.analysis.cameraFrame.mobile : layout.analysis.cameraFrame.desktop);
      if (target) placement.facingY = facingToward(placement.worldPosition, target);
    } else if (targetWorld) {
      placement.facingY = facingToward(placement.worldPosition, targetWorld);
    }
  }
  return recipes.map((recipe) => {
    const placement = placements.find((item) => item.id === recipe.id);
    if (!placement) throw new Error(`Missing resolved blocking point for ${recipe.id}`);
    return placement;
  });
}

export function blockingScenarioIdsFor(layout: BackgroundLayoutDefinition & { id?: string }): BlockingScenarioId[] {
  return layout.id === 'clubroom' ? [] : BLOCKING_SCENARIOS.map((scenario) => scenario.id);
}
